import { usePageHeader } from "contexts/PageHeaderContext";
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Badge } from '../../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import {
  Calendar,
  Plus,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Package,
  X,
  Check,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useScreenPermission } from "hooks/useScreenPermission";
import useDebounce from 'hooks/useDebounce';
import { toast } from 'sonner';
import { deleteMiqaatConfig, getMiqaatConfigList, getMiqaatStatics } from 'services/miqaatConfigService';
import { ApiMiqaat, Statics } from 'types/miqaatConfig';
import { FullScreenLoading } from 'components/ui/loader';
import ConfirmDialog from 'ui/ConfirmDialog';
import Pagination from 'ui/pagination';


export function MiqaatConfiguration() {

  const navigate = useNavigate()
  const { canAdd, canEdit, canDelete, screenName } = useScreenPermission();
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: screenName,
      childTitle: "Manage miqaat packages and rotations",
      icon: <Calendar className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader, screenName]);
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [expandedMiqaatRows, setExpandedMiqaatRows] = useState<string[]>([]); // Track expanded miqaat rows in list view
  const [loading, setLoading] = useState(false);
  const [miqaatConfigs, setMiqaatConfigs] = useState<ApiMiqaat[]>([]);
  const [miqaatTotalCount, setMiqaatTotalCount] = useState(0);
  const [miqaatDeleteId, setMiqaatDeleteId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statics, setStatics] = useState<Statics | null>(null)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    razaPackage: '',
    miqaatName: '',
    miqaatCode: '',
    rotationName: '',
    rotationCode: '',
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      searchTerm,
    }),
    [filters, searchTerm],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Toggle miqaat row expansion in list view
  const toggleMiqaatRowExpansion = (miqaatId: string) => {
    setExpandedMiqaatRows((prev) =>
      prev.includes(miqaatId) ? prev.filter((id) => id !== miqaatId) : [...prev, miqaatId]
    );
  };


  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };


  const sortedApplicants = useMemo(() => {
    if (!sortColumn) return miqaatConfigs;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...miqaatConfigs].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      return sortDirection === "asc"
        ? comparison
        : -comparison;
    });
  }, [miqaatConfigs, sortColumn, sortDirection]);

  //API CALLS START

  const getMiqaatDashboardCounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMiqaatStatics();
      setStatics(data)
    } catch {
      toast.error("Failed to fetch venues");
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    getMiqaatDashboardCounts()
  }, [getMiqaatDashboardCounts]);


  const fetchMiqaatList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMiqaatConfigList(debouncedFilters);
      setMiqaatConfigs(data.items);
      setMiqaatTotalCount(data.totalCount);
    } catch {
      toast.error("Failed to fetch Ziyarat");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);


  useEffect(() => {
    fetchMiqaatList();
  }, [fetchMiqaatList]);
  // API CALLS END


  // DELETE MIQAAT FUNCTIONLITY START
  const handleDeleteMiqaat = (id: string) => {
    setMiqaatDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const cancelDeleteDraft = () => {
    setDeleteDialogOpen(false);
    setMiqaatDeleteId(null);
  };

  const confirmDeleteMiqaat = async () => {
    if (!miqaatDeleteId) return;
    try {
      setLoading(true);
      await deleteMiqaatConfig(miqaatDeleteId);
      toast.success("Miqaat deleted successfully");
      await fetchMiqaatList();
      setDeleteDialogOpen(false);
      setMiqaatDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };
  // DELETE ZIYARAT FUNCTIONLITY END


  // LIST VIEW
  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStatistics(!showStatistics)}
            className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
          >
            {showStatistics ? <ArrowUp className="w-4 h-4 mr-2" /> : <ArrowDown className="w-4 h-4 mr-2" />}
            Statistics
          </Button>
          {canAdd && (
            <Button
              onClick={() => { navigate("/miqaat-master/add") }}
              style={{ background: '#5B9BD5' }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Miqaat
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      {showStatistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Miqaats */}
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Miqaats</p>
                  <p className="text-2xl font-bold text-blue-900">{statics?.totalMiqaats || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Rotations */}
          <Card className="border-indigo-200 bg-indigo-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-600">Total Rotations</p>
                  {/* <p className="text-2xl font-bold text-indigo-900">{stats.totalRotations}</p> */}
                  <p className="text-2xl font-bold text-indigo-900">{statics?.totalRotations || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <ArrowUpDown className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* With Raza */}
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">With Raza</p>
                  {/* <p className="text-2xl font-bold text-purple-900">{stats.withRaza}</p> */}
                  <p className="text-2xl font-bold text-purple-900">{statics?.withRaza || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active */}
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Active</p>
                  <p className="text-2xl font-bold text-green-900">{statics?.totalActive || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="border-[#5B9BD5]/30">
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search by miqaat name or code..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setFilters(prev => ({ ...prev, pageNo: 1 }))
                }}
                className="pl-10"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              style={showFilters ? { background: '#5B9BD5' } : {}}
              className={showFilters ? "text-white" : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"}
              title="Toggle Advanced Filters"
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Section */}
      {showFilters && (
        <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Filter miqaats by multiple criteria
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="filter-raza" className="text-sm">Require Raza</Label>
                <Select value={filters.razaPackage} onValueChange={(value) => setFilters({ ...filters, razaPackage: value, pageNo: 1 })}>
                  <SelectTrigger id="filter-raza" className="bg-input-background h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="*">All</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="filter-miqaat-name" className="text-sm">Miqaat Name</Label>
                <Input
                  id="filter-miqaat-name"
                  placeholder="Filter by miqaat name..."
                  value={filters.miqaatName}
                  onChange={(e) => setFilters({ ...filters, miqaatName: e.target.value, pageNo: 1 })}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="filter-miqaat-code" className="text-sm">Miqaat Code</Label>
                <Input
                  id="filter-miqaat-code"
                  placeholder="Filter by miqaat code..."
                  value={filters.miqaatCode}
                  onChange={(e) => setFilters({ ...filters, miqaatCode: e.target.value, pageNo: 1 })}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="filter-rotation-name" className="text-sm">Rotation Name</Label>
                <Input
                  id="filter-rotation-name"
                  placeholder="Filter by rotation name..."
                  value={filters.rotationName}
                  onChange={(e) => setFilters({ ...filters, rotationName: e.target.value, pageNo: 1 })}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="filter-rotation-code" className="text-sm">Rotation Code</Label>
                <Input
                  id="filter-rotation-code"
                  placeholder="Filter by rotation code..."
                  value={filters.rotationCode}
                  onChange={(e) => setFilters({ ...filters, rotationCode: e.target.value, pageNo: 1 })}
                  className="h-9 text-sm"
                />
              </div>
            </div>

            {/* Active Filters Display */}
            {(filters.razaPackage !== '' || filters.miqaatName || filters.miqaatCode || filters.rotationName || filters.rotationCode) && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.razaPackage !== '' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Raza: {filters.razaPackage === 'true' ? 'Yes' : 'No'}
                      </Badge>
                    )}
                    {filters.miqaatName && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Miqaat Name: {filters.miqaatName}
                      </Badge>
                    )}
                    {filters.miqaatCode && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Miqaat Code: {filters.miqaatCode}
                      </Badge>
                    )}
                    {filters.rotationName && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Rotation Name: {filters.rotationName}
                      </Badge>
                    )}
                    {filters.rotationCode && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Rotation Code: {filters.rotationCode}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilters({
                      razaPackage: '',
                      miqaatName: '',
                      miqaatCode: '',
                      rotationName: '',
                      rotationCode: '',
                      pageNo: 1,
                      pageSize: 5
                    });
                  }}
                  className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Miqaat List */}
      <Card className="bg-white border-[#5B9BD5]/30">
        <CardHeader className="p-4 border-b bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-xl">
          <CardTitle style={{ color: '#5B9BD5' }}>Miqaat Packages</CardTitle>
          <CardDescription>
            {miqaatTotalCount} miqaat{miqaatTotalCount !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: '#E3F2FD' }}>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs w-10"></TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('miqaatName')}
                  >
                    <div className="flex items-center gap-1">
                      Miqaat Name
                      {sortColumn === 'miqaatName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('miqaatCode')}
                  >
                    <div className="flex items-center gap-1">
                      Miqaat Code
                      {sortColumn === 'miqaatCode' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50 text-center"
                    onClick={() => handleSort('requireRaza')}
                  >
                    <div className="flex items-center gap-1 justify-center">
                      Require Raza
                      {sortColumn === 'requireRaza' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50 text-center"
                    onClick={() => handleSort('isActive')}
                  >
                    <div className="flex items-center gap-1 justify-center">
                      Status
                      {sortColumn === 'isActive' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  {(canEdit || canDelete) && (
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-right">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {miqaatConfigs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No miqaats found
                    </TableCell>
                  </TableRow>
                ) : (
                  miqaatConfigs && sortedApplicants?.map((miqaat, i) => {
                    const isExpanded = expandedMiqaatRows.includes(miqaat.publicId);
                    // const hasRotations = miqaat.razaPackage && miqaat.rotations && miqaat.rotations.length > 0;
                    // const hasRotations = miqaat.requireRaza && miqaat.rotations && miqaat.rotations.length > 0;
                    const hasRotations = miqaat.rotations && miqaat.rotations.length > 0;

                    return (
                      <Fragment key={miqaat.publicId || miqaat.id}>
                        <TableRow className="hover:bg-sky-50">
                          <TableCell>
                            {hasRotations && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleMiqaatRowExpansion(miqaat.publicId)}
                                className="p-0 h-6 w-6"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-600" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-600" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{miqaat.miqaatName}</TableCell>
                          <TableCell>{miqaat.miqaatCode}</TableCell>
                          <TableCell className="text-center">
                            {miqaat.requireRaza ? (
                              <Badge className="bg-green-100 text-green-700 border-green-300">Yes</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700 border-gray-300">No</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {miqaat.isActive ? (
                              <Badge className="bg-green-100 text-green-700 border-green-300">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700 border-red-300">
                                <XCircle className="w-3 h-3 mr-1" />
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-2 justify-end">
                              {canEdit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#5B9BD5] hover:bg-blue-50 h-8"
                                  onClick={() => { navigate(`/miqaat-master/edit/${miqaat.publicId}`) }}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteMiqaat(String(miqaat.publicId))}
                                  className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Rotations Row */}
                        {isExpanded && hasRotations && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-blue-50/30 p-0">
                              <div className="p-4">
                                <div className="bg-white border border-[#5B9BD5]/20 rounded-lg overflow-hidden">
                                  <div className="bg-blue-50 border-b border-[#5B9BD5]/20">
                                    <div className="grid grid-cols-4 gap-4 p-3">
                                      <div className="text-sm font-medium text-[#5B9BD5]">Rotation Details</div>
                                      <div className="text-sm font-medium text-[#5B9BD5]">Arrival</div>
                                      <div className="text-sm font-medium text-[#5B9BD5]">Departure</div>
                                      <div className="text-sm font-medium text-[#5B9BD5]">Quota</div>
                                    </div>
                                  </div>
                                  <div className="divide-y divide-gray-100">
                                    {miqaat.rotations.map((rotation, i) => (
                                      <div key={rotation.publicId || i} className="grid grid-cols-4 gap-4 p-3 hover:bg-gray-50">
                                        <div>
                                          <div className="font-medium text-gray-900">{rotation.rotationName}</div>
                                          <div className="text-xs text-gray-500">{rotation.rotationCode}</div>
                                        </div>
                                        <div className="text-sm text-gray-900">
                                          {new Date(rotation.arrivalDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                                        </div>
                                        <div className="text-sm text-gray-900">
                                          {new Date(rotation.departureDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                                        </div>
                                        <div className="text-sm">
                                          <span className="font-semibold text-gray-900">{rotation.quota}</span>
                                          <div className="text-xs text-gray-500">
                                            Allocated: <span className="font-medium">{rotation.totalAllocated}</span> | Remaining: <span className="font-medium text-green-600">{rotation.remainingQuota}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden p-4 space-y-3">
            {miqaatConfigs.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No miqaats found</div>
            ) : (
              miqaatConfigs.map((miqaat, i) => {
                const isExpanded = expandedMiqaatRows.includes(miqaat.id);
                // const hasRotations = miqaat.razaPackage && miqaat.rotations && miqaat.rotations.length > 0;
                const hasRotations = miqaat.rotations && miqaat.rotations.length > 0;

                return (
                  <div key={miqaat.publicId || i} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-900">{miqaat.miqaatName}</div>
                          <div className="text-sm text-gray-500">{miqaat.miqaatCode}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#5B9BD5] hover:bg-blue-50 h-8 w-8 p-0"
                            onClick={() => { navigate(`/miqaat-master/edit/${miqaat.publicId}`) }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteMiqaat(String(miqaat.publicId))}
                            className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Raza:</span>
                          {miqaat.razaPackage ? (
                            <Badge className="bg-green-100 text-green-700 border-green-300">Yes</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-700 border-gray-300">No</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status:</span>
                          {miqaat.isActive ? (
                            <Badge className="bg-green-100 text-green-700 border-green-300">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-red-300">
                              <XCircle className="w-3 h-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Show rotations button if applicable */}
                      {hasRotations && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleMiqaatRowExpansion(miqaat.id)}
                          className="w-full text-[#5B9BD5] border border-[#5B9BD5]/20 h-8 mt-2"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronDown className="w-4 h-4 mr-2" />
                              Hide Rotations
                            </>
                          ) : (
                            <>
                              <ChevronRight className="w-4 h-4 mr-2" />
                              View Rotations ({miqaat.rotations.length})
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Expanded Rotations */}
                    {isExpanded && hasRotations && (
                      <div className="border-t border-gray-200 bg-blue-50/30 p-3 space-y-2">
                        {miqaat.rotations.map((rotation, index) => (
                          <div key={rotation.publicId || index} className="bg-white border border-[#5B9BD5]/20 rounded-lg p-3 space-y-2">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-medium text-gray-900">{rotation.rotationName}</div>
                                <div className="text-xs text-gray-500">{rotation.rotationCode}</div>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-gray-900">{rotation.quota}</div>
                                <div className="text-xs text-gray-500">Total</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-xs text-gray-500">Arrival:</span>
                                <div className="font-medium text-gray-900">
                                  {new Date(rotation.arrivalDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                                </div>
                              </div>
                              <div>
                                <span className="text-xs text-gray-500">Departure:</span>
                                <div className="font-medium text-gray-900">
                                  {new Date(rotation.departureDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 rounded p-2 text-xs flex justify-between">
                              <span>Allocated: <span className="font-medium">{rotation.totalAllocated}</span></span>
                              <span>Remaining: <span className="font-medium text-green-600">{rotation.remainingQuota}</span></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {/* PAGINATION COMPONENT FOR DRAFT */}
          <Pagination
            totalItems={miqaatTotalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((prev) => ({
                ...prev,
                pageNo: page, // convert back to 0-based
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1, // IMPORTANT: reset page
              }))
            }
            label=""
          />
        </CardContent>
      </Card>

      {/* DELETE CONFIRMATION POPUP */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Confirm Delete"
        description="Are you sure you want to delete?"
        confirmText="Yes, Delete"
        // loading={isDeleting}
        onConfirm={confirmDeleteMiqaat}
        onCancel={cancelDeleteDraft} // ✅ NOW IT WORKS
      >
      </ConfirmDialog>
    </div>
  );
}